import React, { useEffect, useState } from 'react';
import { Loader2, FileWarning, Palette, Zap, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { OrkaSurface, OrkaText, OrkaBadge } from '../orka';

/**
 * StyleViolationsPanel — Phase 7 enforcement scoreboard for the V3 design system.
 *
 * Surfaces the `core/audit/legacy_styles.py` scan results inside the Architecture
 * Audit dashboard. Shows per-file Rule-1 (raw colours) and Rule-7 (orka-transition-colors)
 * counts so admins can track migration progress as the cascade rolls out.
 */
export default function StyleViolationsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/core/audit/style-violations', { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="py-12 flex flex-col items-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mb-2" />
        <span className="text-xs">Scanning frontend tree…</span>
      </div>
    );
  }
  if (error) {
    return <OrkaSurface variant="raised" radius="lg" className="p-4 orka-text-danger" data-testid="style-violations-error">{error}</OrkaSurface>;
  }
  if (!data) return null;

  const { summary, file_scoreboard, rule_1_samples, rule_7_samples } = data;

  return (
    <div className="space-y-4" data-testid="style-violations-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <OrkaSurface variant="raised" radius="lg" className="p-3" testId="style-stat-rule1">
          <div className="flex items-center gap-2">
            <Palette className="w-4 h-4 orka-text-warning" />
            <OrkaText size="micro" variant="tertiary">Rule 1 — Raw colours</OrkaText>
          </div>
          <div className="mt-1 text-2xl font-mono font-bold orka-text-warning tabular-nums">
            {summary.rule_1_count.toLocaleString()}
          </div>
        </OrkaSurface>
        <OrkaSurface variant="raised" radius="lg" className="p-3" testId="style-stat-rule7">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 orka-text-info" />
            <OrkaText size="micro" variant="tertiary">Rule 7 — orka-transition-colors</OrkaText>
          </div>
          <div className="mt-1 text-2xl font-mono font-bold orka-text-info tabular-nums">
            {summary.rule_7_count.toLocaleString()}
          </div>
        </OrkaSurface>
        <OrkaSurface variant="raised" radius="lg" className="p-3">
          <OrkaText size="micro" variant="tertiary">Files scanned</OrkaText>
          <div className="mt-1 text-2xl font-mono font-bold orka-text-primary tabular-nums">{summary.files_scanned}</div>
        </OrkaSurface>
        <OrkaSurface variant="raised" radius="lg" className="p-3">
          <OrkaText size="micro" variant="tertiary">DS-internal whitelisted</OrkaText>
          <div className="mt-1 text-2xl font-mono font-bold orka-text-success tabular-nums">{summary.files_whitelisted}</div>
        </OrkaSurface>
      </div>

      <div className="flex items-center justify-between">
        <OrkaText size="small" variant="secondary">
          Per-file scoreboard (top {file_scoreboard.length}). Migration target: <span className="orka-text-primary font-medium">0</span> per file.
        </OrkaText>
        <Button variant="outline" size="sm" onClick={load} className="h-8 gap-1" data-testid="style-violations-refresh">
          <RefreshCw className="w-3 h-3" />
          Re-scan
        </Button>
      </div>

      {/* File scoreboard */}
      <OrkaSurface variant="raised" radius="lg" bordered className="overflow-hidden">
        <table className="w-full text-xs">
          <thead className="orka-surface-subtle">
            <tr>
              <th className="text-left p-2 font-semibold">File</th>
              <th className="text-right p-2 font-semibold w-20">Rule 1</th>
              <th className="text-right p-2 font-semibold w-20">Rule 7</th>
              <th className="text-right p-2 font-semibold w-20">Total</th>
            </tr>
          </thead>
          <tbody>
            {file_scoreboard.map((f) => {
              const total = f.rule_1 + f.rule_7;
              return (
                <tr key={f.file} className="border-t orka-border-subtle">
                  <td className="p-2 font-mono">{f.file}</td>
                  <td className="p-2 text-right font-mono">
                    {f.rule_1 > 0 ? <span className="orka-text-warning">{f.rule_1}</span> : <span className="orka-text-tertiary">0</span>}
                  </td>
                  <td className="p-2 text-right font-mono">
                    {f.rule_7 > 0 ? <span className="orka-text-info">{f.rule_7}</span> : <span className="orka-text-tertiary">0</span>}
                  </td>
                  <td className="p-2 text-right">
                    <OrkaBadge tone={total > 100 ? 'danger' : total > 30 ? 'warning' : 'neutral'} size="sm">{total}</OrkaBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </OrkaSurface>

      {/* Sample violations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <OrkaSurface variant="raised" radius="lg" className="p-3">
          <OrkaText size="h3" variant="primary" as="h4" className="block mb-2">Rule 1 samples</OrkaText>
          <ul className="space-y-1 max-h-72 overflow-y-auto">
            {rule_1_samples.map((v, i) => (
              <li key={i} className="text-[11px] font-mono">
                <span className="orka-text-tertiary">{v.file}:{v.line}</span>
                {' '}<Badge variant="outline" className="text-[9px]">{v.match}</Badge>
                <div className="orka-text-secondary truncate">{v.snippet}</div>
              </li>
            ))}
          </ul>
        </OrkaSurface>
        <OrkaSurface variant="raised" radius="lg" className="p-3">
          <OrkaText size="h3" variant="primary" as="h4" className="block mb-2">Rule 7 samples</OrkaText>
          {rule_7_samples.length === 0 ? (
            <OrkaText size="small" variant="success" className="flex items-center gap-1.5">
              <FileWarning className="w-4 h-4" /> No orka-transition-colors violations
            </OrkaText>
          ) : (
            <ul className="space-y-1 max-h-72 overflow-y-auto">
              {rule_7_samples.map((v, i) => (
                <li key={i} className="text-[11px] font-mono">
                  <span className="orka-text-tertiary">{v.file}:{v.line}</span>
                  <div className="orka-text-secondary truncate">{v.snippet}</div>
                </li>
              ))}
            </ul>
          )}
        </OrkaSurface>
      </div>
    </div>
  );
}
